import { createSlice, createSelector, createAsyncThunk } from '@reduxjs/toolkit';
import { PRODUCTS_BASE_DATA, findProductById, getAllFlattenedProducts } from '../data/productsData';
// ============================================
// ASYNC THUNKS (Mock API Calls)
// ============================================

export const fetchProductsData = createAsyncThunk(
  'products/fetchProductsData',
  async () => {
    await new Promise(resolve => setTimeout(resolve, 300));
    return PRODUCTS_BASE_DATA;
  }
);

export const fetchProductById = createAsyncThunk(
  'products/fetchProductById',
  async (productId) => {
    //console.log("Fetching product with ID:", productId);
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const result = findProductById(productId);
    if (!result) {
      throw new Error(`Product ${productId} not found`);
    }
    
    return result;
  }
);

// ============================================
// REDUX SLICE
// ============================================

const productsSlice = createSlice({
  name: 'products',
  initialState: {
    // OCXO Analysis related state
    selectedFrequency: '8.000000',
    selectedMultiplier: 'Fundamental',
    selectedSupplySignal: 'hcmos3p3V',
    selectedSupply: '3.3V',
    selectedSignal: 'HCMOS',
    activeTempRangesTab: 0,
    selectedTemperatureRanges: '5',//++
    selectedAgingOptions: '50', //++
    selectedStabilityOptions: '50', //++

    // Custom performance factors
    customStabilityFactor: '', // Changed from null
    customAgingFactor: '',     // Changed from null
    customConsumptionFactor: '', // Changed from null


    // Old form fields (kept for backward compatibility)
    formPartNumber: '', //result of the selections
    formInputFrequency: 8,
    formInputFrequencyUnderlineTxt: null,
    formSelectedMultiplier: 'Fundamental',
    formSelectedSupplySignal: 'hcmos3p3V',
    formSelectedSupply: '3.3V',
    formSelectedSignal: 'HCMOS',
    formInputStability: null,
    //formInputStabilityUnderlineTxt: null, // createSelector
    formInputAging: null,
    formInputAgingUnderlineTxt: null,
    formInputPowerConsumption: null,
    formInputPowerConsumptionUnderlineTxt: null,
    formAdditionalRequirements: '',

    // Product data
    imagePath: "/pimages/img_axl/02_products",
    productGroups: [],
    allProducts: [],
    currentProduct: null,
    relativeProducts: null,
    parentGroup: null,
    testField: null,
    testField2: null,
    loading: false,
    loadingProduct: false,
    error: null,
    errorProduct: null,
    activeGroups: [],
    pdfDownloads: {},
    isMobile: false
  },
  reducers: {
    // OCXO Analysis reducers
    setSelectedFrequency: (state, action) => {
      state.selectedFrequency = action.payload;
    },
    setSelectedMultiplier: (state, action) => {
      state.selectedMultiplier = action.payload;
    },
    setSelectedSupplySignal: (state, action) => {
      state.selectedSupplySignal = action.payload;
    },   
    setSelectedSupply: (state, action) => {
      state.selectedSupply = action.payload;
    },
    setSelectedSignal: (state, action) => {
      state.selectedSignal = action.payload;
    },
    setActiveTempRangesTab: (state, action) => {
      state.activeTempRangesTab = action.payload;
    },
    setCustomStabilityFactor: (state, action) => {
      state.customStabilityFactor = action.payload;
    },
    setCustomAgingFactor: (state, action) => {
      state.customAgingFactor = action.payload;
    },
    setCustomConsumptionFactor: (state, action) => {
      state.customConsumptionFactor = action.payload;
    },
    
    // Old form reducers (kept for backward compatibility)
    setActiveGroups: (state, action) => {
      state.activeGroups = action.payload;
    },
    toggleProductGroup: (state, action) => {
      const groupId = action.payload;
      if (state.activeGroups.includes(groupId)) {
        state.activeGroups = state.activeGroups.filter(id => id !== groupId);
      } else {
        state.activeGroups.push(groupId);
      }
    },
    setActiveTempRangesTab: (state, action) => {
      state.activeTempRangesTab = action.payload;
    },
    setFormInputFrequency: (state, action) => {
      state.formInputFrequency = action.payload;
      //state.stabilityUnderlineText = calculateStabilityText(state);
    },
    setFormInputFrequencyUnderlineTxt: (state, action) => {
      state.formInputFrequencyUnderlineTxt = action.payload;
    },    
    setFormSelectedMultiplier: (state, action) => {
      state.formSelectedMultiplier = action.payload;
    },
    setFormSelectedSupplySignal: (state, action) => {
      state.formSelectedSupplySignal = action.payload;
    },
    setFormPartNumber: (state, action) => {
      state.formPartNumber = action.payload;
    },    
    setFormSelectedSupply: (state, action) => {
      state.formSelectedSupply = action.payload;
    },
    setFormSelectedSignal: (state, action) => {
      state.formSelectedSignal = action.payload;
    },
    setFormInputStability: (state, action) => {
      state.formInputStability = action.payload;
    },
    setFormAdditionalRequirements: (state, action) => {
      state.formAdditionalRequirements = action.payload;
    },    
      // Add new reducers for form data
    setSelectedTemperatureRanges: (state, action) => {
      state.selectedTemperatureRanges = action.payload;
    },
    setSelectedAgingOptions: (state, action) => {
      state.selectedAgingOptions = action.payload;
    },
    setSelectedStabilityOptions: (state, action) => {
      state.selectedStabilityOptions = action.payload;
    },
    
/*     setFormInputStabilityUnderlineTxt: (state, action) => {
      state.formInputStabilityUnderlineTxt = action.payload;
    }, */
    setFormInputAging: (state, action) => {
      state.formInputAging = action.payload;
    },
    setFormInputAgingUnderlineTxt: (state, action) => {
      state.formInputAgingUnderlineTxt = action.payload;
    },
    setFormInputPowerConsumption: (state, action) => {
      state.formInputPowerConsumption = action.payload;
    },
    setFormInputPowerConsumptionUnderlineTxt: (state, action) => {
      state.formInputPowerConsumptionUnderlineTxt = action.payload;
    }, 
    setIsMobile: (state, action) => {
      state.isMobile = action.payload;
    },
    trackPdfDownload: (state, action) => {
      const { groupId, productName } = action.payload;
      if (!state.pdfDownloads[groupId]) {
        state.pdfDownloads[groupId] = [];
      }
      state.pdfDownloads[groupId].push({
        productName,
        timestamp: new Date().toISOString()
      });
    },
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
      state.parentGroup = null;
      state.errorProduct = null;
      // Reset OCXO analysis when clearing product
      state.selectedFrequency = '8.000000';
      state.selectedMultiplier = 'Fundamental';
      state.selectedSupplySignal = 'sWave3p3V';
      state.selectedSupply = '3.3V';
      state.selectedSignal = 'HCMOS';
      state.activeTempRangesTab = 0;
      state.customStabilityFactor = ''; // Reset to string, not null
      state.customAgingFactor = '';    // Reset to string, not null
      state.customConsumptionFactor = ''; // Reset to string, not null
      state.selectedTemperatureRanges = '5';//++
      state.selectedAgingOptions = '50'; //++
      state.selectedStabilityOptions = '50'; //++
      state.formAdditionalRequirements = '';
    },

    setCurrentProduct: (state, action) => {
      const productId = action.payload;
      
      const product = state.allProducts.find(p => p.id === productId);
      if (product) {
        state.currentProduct = product;
        const group = state.productGroups.find(g => g.id === product.groupId);
        if (group) {
          state.parentGroup = {
            id: group.id,
            caption: group.caption,
            pdfUrl: group.pdfUrl,
            specifications: group.specifications,
            //isLowPower: group.isLowPower,
            color: group.color,
            headerGradient: group.headerGradient
          };
        }
      }
    },
    initializeActiveGroups: (state) => {
      if (state.isMobile && state.productGroups.length > 0) {
        state.activeGroups = state.productGroups.map(g => g.id);
      } else {
        state.activeGroups = [];
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProductsData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductsData.fulfilled, (state, action) => {
        state.loading = false;
        state.productGroups = action.payload;
        state.allProducts = getAllFlattenedProducts();
      })
      .addCase(fetchProductsData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchProductById.pending, (state) => {
        state.loadingProduct = true;
        state.errorProduct = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.loadingProduct = false;
        state.currentProduct = action.payload.product;
        state.relativeProducts = action.payload.relativeProducts;
        state.parentGroup = action.payload.parentGroup;
        state.testField = action.payload.testField;        
        if (action.payload.product.partNumberServiceData) {
          const d = action.payload.product.partNumberServiceData
          //array: [8,300] -> "8"
          state.selectedFrequency = d.frequencyInput.array[0].toString();
          //{ code: "5", range: "0..50ºC" }, -> "5"
          state.selectedTemperatureRanges = d.temperatureRanges[0].code.toString();
          //{ code: "29", ppbPerDay: "2" }, -> "29"
          state.selectedAgingOptions = d.agingOptions[0].code.toString();
          // { code: "50", value: "0.5", unit: "ppb" }, -> "50"
          state.selectedAgingOptions = d.stabilityOptions[0].code.toString();
        }

        // Set default frequency from product if available
        if (action.payload.product?.data?.frequencyMin) {
          state.selectedFrequency = action.payload.product.data.frequencyMin.toString();
        }
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loadingProduct = false;
        state.errorProduct = action.error.message;
      });
  },
});

// ============================================
// SELECTORS
// ============================================

// OCXO Analysis selectors
export const selectSelectedFrequency = (state) => state.products.selectedFrequency;
export const selectSelectedMultiplier = (state) => state.products.selectedMultiplier;
export const selectAvailableMultipliers = (state) => state.products.currentProduct?.availableMultipliers;
export const selectCurrProdName = (state) => state.products.currentProduct?.name;
export const selectModelParamsComponentCode = (state) => state.products.currentProduct?.modelParamsComponentCode;
export const selectSelectedSupplySignal = (state) => state.products.selectedSupplySignal;
export const selectSelectedSupply = (state) => state.products.selectedSupply;
export const selectSelectedSignal = (state) => state.products.selectedSignal;
export const selectActiveTempRangesTab = (state) => state.products.activeTempRangesTab;
export const selectCustomStabilityFactor = (state) => state.products.customStabilityFactor;
export const selectCustomAgingFactor = (state) => state.products.customAgingFactor;
export const selectCustomConsumptionFactor = (state) => state.products.customConsumptionFactor;

export const selectSelectedTemperatureRanges = (state) => state.products.selectedTemperatureRanges;
export const selectSelectedAgingOptions = (state) => state.products.selectedAgingOptions;
export const selectSelectedStabilityOptions = (state) => state.products.selectedStabilityOptions;

// Old form selectors (kept for backward compatibility)
export const selectFormInputFrequency = (state) => state.products.formInputFrequency;
export const selectFormInputFrequencyUnderlineTxt = (state) => state.products.formInputFrequencyUnderlineTxt;
export const selectFormSelectedMultiplier = (state) => state.products.formSelectedMultiplier;
export const selectFormSelectedSupplySignal = (state) => state.products.formSelectedSupplySignal;
export const selectFormPartNumber = (state) => state.products.formPartNumber;
export const selectFormSelectedSupply = (state) => state.products.formSelectedSupply;
export const selectFormSelectedSignal = (state) => state.products.formSelectedSignal;
export const selectFormInputStability = (state) => state.products.formInputStability;
export const selectFormAdditionalRequirements = (state) => state.products.formAdditionalRequirements;

export const selectRelativeProducts = (state) => state.products.relativeProducts;
//export const selectFormInputStabilityUnderlineTxt = (state) => state.products.formInputStabilityUnderlineTxt;
export const selectFormInputAging = (state) => state.products.formInputAging;
//export const selectFormInputAgingUnderlineTxt = (state) => state.products.formInputAgingUnderlineTxt;
export const selectFormInputPowerConsumption = (state) => state.products.formInputPowerConsumption;
//export const selectFormInputPowerConsumptionUnderlineTxt = (state) => state.products.formInputPowerConsumptionUnderlineTxt;

// Product data selectors
export const selectProductGroups = (state) => state.products.productGroups;
export const selectActiveGroups = (state) => state.products.activeGroups;
export const selectProductsLoading = (state) => state.products.loading;
export const selectProductsError = (state) => state.products.error;
export const selectIsMobile = (state) => state.products.isMobile;
export const selectPdfDownloads = (state) => state.products.pdfDownloads;
export const selectCurrentProduct = (state) => state.products.currentProduct;
export const selectParentGroup = (state) => state.products.parentGroup;
export const selectAllProducts = (state) => state.products.allProducts;
export const selectLoadingProduct = (state) => state.products.loadingProduct;
export const selectErrorProduct = (state) => state.products.errorProduct;
export const selectImagePath = (state) => state.products.imagePath;


export const selectProductById = (productId) => (state) => 
  state.products.allProducts.find(p => p.id === productId);

/* export const selectFormInputStabilityUnderlineTxt = createSelector(
  [selectActiveTempRangesTab, selectIsMobile],
  (f, m) => {
    //if (stability === null) return 'Enter frequency';
    //return `Base: ${stability.toFixed(2)} ppb (×${customFactor})`;
    return "Hello from selector";
  }
); */


// Helper selector to get OCXO data from current product
export const selectOcxoData = (state) => {
  const product = state.products.currentProduct;
  if (!product || !product.data) return null;
  
  return {
    frequencyMin: product.frequencyMin,
    frequencyMax: product.frequencyMax,
    tempRanges: product.tempRanges || ["0..50ºC", "-10..60ºC", "-30..70ºC", "-40..85ºC", "-60..85ºC"],
    signalTypes: product.signalTypes || ["sWave3p3V", "sWave5V", "hcmos3p3V", "hcmos5V"],
    availableMultipliers: product.availableMultipliers || ['Fundamental'],
    data: product.data
  };
};

// ============================================
// ACTION EXPORTS
// ============================================

export const { 
  // OCXO Analysis actions
  setSelectedFrequency,
  setSelectedMultiplier,
  setSelectedSupplySignal,
  setSelectedSupply,
  setSelectedSignal,
  setActiveTempRangesTab,
  setCustomStabilityFactor,
  setCustomAgingFactor,
  setCustomConsumptionFactor,
  
  setSelectedTemperatureRanges,
  setSelectedAgingOptions,
  setSelectedStabilityOptions,
  
  // Old form actions (kept for backward compatibility)
  setActiveGroups, 
  toggleProductGroup, 
  setFormInputFrequency,
  setFormInputFrequencyUnderlineTxt,
  setFormSelectedMultiplier,
  setFormSelectedSupplySignal,
  setFormPartNumber,
  setFormSelectedSupply,
  setFormSelectedSignal,
  setFormInputStability,
  //setFormInputStabilityUnderlineTxt,
  setFormInputAging,
  //setFormInputAgingUnderlineTxt,
  setFormInputPowerConsumption,
  setFormAdditionalRequirements,
  //setFormInputPowerConsumptionUnderlineTxt,
  
  // Common actions
  setIsMobile,
  trackPdfDownload,
  clearCurrentProduct,
  initializeActiveGroups,
} = productsSlice.actions;

export default productsSlice.reducer;

